import { useCallback } from 'react';

import { SortableColumnsConfig } from '@/components/table/sortableColumns/types';
import { Column } from '@/components/table/types';
import { useSortableColumn } from '@/hooks/useSortableColumn';

/**
 * Configuration for smart column positioning rules
 *
 * Defines where columns should be positioned when they are toggled on.
 * Does NOT affect drag-to-reorder operations - user drag positions are preserved.
 */
export interface ColumnPositioningRules {
  /**
   * Column IDs that should go to first position(s) when toggled on
   *
   * @example
   * ```ts
   * firstColumns: ['status'] // Status column returns to first position
   * ```
   */
  firstColumns?: string[];

  /**
   * Column IDs that should appear right before the last columns when toggled on
   *
   * Useful for metadata columns like tags that should appear after core columns
   * but before date columns.
   *
   * @example
   * ```ts
   * beforeLastColumns: ['tags'] // Tags column appears before created/visited dates
   * ```
   */
  beforeLastColumns?: string[];

  /**
   * Column IDs that should go to last position(s) when toggled on
   *
   * @example
   * ```ts
   * lastColumns: ['created', 'visited'] // Date columns go to end
   * ```
   */
  lastColumns?: string[];

  /**
   * Column IDs to insert after a specific anchor column when toggled on
   *
   * If anchor column is not found, columns are appended to end.
   *
   * @example
   * ```ts
   * afterColumn: {
   *   ids: ['priority', 'cvss', 'epss'],
   *   after: 'name' // Insert these after 'name' column
   * }
   * ```
   */
  afterColumn?: {
    ids: string[];
    after: string;
  };
}

/**
 * Parameters for useSmartColumnPositioning hook
 */
export interface UseSmartColumnPositioningProps<TData> {
  /** Storage key for persisting column state */
  key: string;

  /** Default column definitions */
  defaultColumns: Column<TData>[];

  /** Sortable columns configuration */
  defaultConfig: SortableColumnsConfig;

  /** Smart positioning rules to apply when columns are toggled on */
  positioningRules: ColumnPositioningRules;
}

/**
 * Return type for useSmartColumnPositioning hook
 */
export interface UseSmartColumnPositioningReturn<TData> {
  /** Currently active column IDs in display order */
  activeColumns: string[] | undefined;

  /** Function to update active columns with smart positioning */
  setActiveColumns: (newColumns: string[]) => void;

  /** Rendered table columns in correct order */
  columns: Column<TData>[];
}

/**
 * Enhanced column management hook with smart default positioning
 *
 * Wraps `useSortableColumn` to provide intelligent column positioning when columns
 * are toggled on, while preserving user drag-to-reorder preferences.
 *
 * ## Key Behaviors
 *
 * **When columns are ADDED (toggled on):**
 * - Applies positioning rules defined in `positioningRules`
 * - Example: Status column returns to first position
 * - Example: Date columns (created, visited) return to last positions
 *
 * **When columns are DRAGGED:**
 * - ENFORCES: `firstColumns` always remain first (e.g., status cannot be moved)
 * - Other columns preserve exact order from user's drag operation
 * - User intent is respected for non-first columns
 *
 * **When columns are REMOVED:**
 * - No positioning changes - remaining columns stay in place
 *
 * ## Positioning Rules Priority
 *
 * When multiple columns are added simultaneously, they are positioned in this order:
 * 1. `firstColumns` - Prepended to start (maintains order from config)
 * 2. Existing columns - Preserved in current order
 * 3. `afterColumn.ids` - Inserted after anchor column (or end if anchor not found)
 * 4. `beforeLastColumns` - Inserted right before lastColumns (NEW)
 * 5. `lastColumns` - Appended to end (maintains order from config)
 *
 * @template TData - Type of data displayed in table rows
 *
 * @example Basic usage with status and date columns
 * ```typescript
 * const { columns, activeColumns, setActiveColumns } = useSmartColumnPositioning({
 *   key: 'assets',
 *   defaultColumns,
 *   defaultConfig,
 *   positioningRules: {
 *     firstColumns: ['status'],           // Status first when toggled on
 *     lastColumns: ['created', 'visited'], // Dates last when toggled on
 *   },
 * });
 * ```
 *
 * @example Advanced usage with anchor positioning
 * ```typescript
 * const { columns, activeColumns, setActiveColumns } = useSmartColumnPositioning({
 *   key: 'vulnerabilities',
 *   defaultColumns,
 *   defaultConfig,
 *   positioningRules: {
 *     firstColumns: ['status'],
 *     lastColumns: ['created', 'visited'],
 *     afterColumn: {
 *       ids: ['priority', 'cvss', 'epss', 'kev', 'exploit'],
 *       after: 'name', // Insert security metrics after name column
 *     },
 *   },
 * });
 * ```
 *
 * @see useAssetColumns for original implementation pattern
 */
export function useSmartColumnPositioning<TData>({
  key,
  defaultColumns,
  defaultConfig,
  positioningRules,
}: UseSmartColumnPositioningProps<TData>): UseSmartColumnPositioningReturn<TData> {
  // Initialize base sortable column hook
  const {
    activeColumns,
    setActiveColumns: originalSetActiveColumns,
    columns,
  } = useSortableColumn<TData>({
    key,
    defaultColumns,
    defaultConfig,
  });

  /**
   * Smart column positioning wrapper
   *
   * Detects newly added columns vs. drag operations and applies positioning rules.
   * ENFORCES that firstColumns always remain first, even during drag/reorder.
   *
   * ## Algorithm
   *
   * 1. Compare new columns to old columns to detect additions
   * 2. If no additions (drag/removal operation):
   *    a. ENFORCE: firstColumns must stay at start (if present)
   *    b. Other columns preserve user's order
   * 3. If additions found:
   *    a. Categorize added columns by positioning rules
   *    b. Start with existing columns (filter out newly added)
   *    c. Insert non-special columns after anchor (if specified)
   *    d. Prepend firstColumns to start
   *    e. Append lastColumns to end
   */
  const setActiveColumns = useCallback(
    (newColumns: string[]) => {
      const oldColumns = activeColumns ?? [];
      const { firstColumns = [] } = positioningRules;

      // Detect newly added columns (toggled on, not dragged)
      const addedColumns = newColumns.filter(id => !oldColumns.includes(id));

      if (addedColumns.length === 0) {
        // No columns added - this is either a drag operation or removal
        // ENFORCE: First columns must always stay first, even during drag operations
        if (firstColumns.length > 0) {
          const firstPresent = newColumns.filter(id =>
            firstColumns.includes(id)
          );
          const others = newColumns.filter(id => !firstColumns.includes(id));

          if (firstPresent.length > 0) {
            // Ensure first columns are at the start in their defined order
            const orderedFirst = firstPresent.sort((a, b) => {
              return firstColumns.indexOf(a) - firstColumns.indexOf(b);
            });
            originalSetActiveColumns([...orderedFirst, ...others]);
            return;
          }
        }

        // No first columns present or no enforcement needed
        originalSetActiveColumns(newColumns);
        return;
      }

      // Apply smart positioning for newly added columns
      const {
        beforeLastColumns = [],
        lastColumns = [],
        afterColumn,
      } = positioningRules;
      // Note: firstColumns already extracted above at line 197

      // Categorize added columns by positioning rules
      const firstAdded = addedColumns.filter(id => firstColumns.includes(id));
      const beforeLastAdded = addedColumns.filter(id =>
        beforeLastColumns.includes(id)
      );
      const lastAdded = addedColumns.filter(id => lastColumns.includes(id));
      const otherAdded = addedColumns.filter(
        id =>
          !firstColumns.includes(id) &&
          !beforeLastColumns.includes(id) &&
          !lastColumns.includes(id)
      );

      // Start with existing columns (columns that weren't newly added)
      let result = oldColumns.filter(id => newColumns.includes(id));

      // 1. Insert non-special columns after anchor (or at end)
      if (afterColumn && otherAdded.length > 0) {
        const anchorIndex = result.indexOf(afterColumn.after);

        if (anchorIndex !== -1) {
          // Anchor found - insert after it
          result = [
            ...result.slice(0, anchorIndex + 1),
            ...otherAdded,
            ...result.slice(anchorIndex + 1),
          ];
        } else {
          // Anchor not found - append to end
          result = [...result, ...otherAdded];
        }
      } else if (otherAdded.length > 0) {
        // No anchor specified - append to end
        result = [...result, ...otherAdded];
      }

      // 2. First columns go to start when added
      if (firstAdded.length > 0) {
        // Preserve order of firstColumns if multiple added simultaneously
        const orderedFirst = firstAdded.sort((a, b) => {
          return firstColumns.indexOf(a) - firstColumns.indexOf(b);
        });
        result = [...orderedFirst, ...result];
      }

      // 3. Before-last columns go right before last columns
      if (beforeLastAdded.length > 0) {
        // Preserve order of beforeLastColumns if multiple added simultaneously
        const orderedBeforeLast = beforeLastAdded.sort((a, b) => {
          return beforeLastColumns.indexOf(a) - beforeLastColumns.indexOf(b);
        });

        // Find the first lastColumn in result to insert before it
        const firstLastColumnIndex = result.findIndex(id =>
          lastColumns.includes(id)
        );

        if (firstLastColumnIndex !== -1) {
          // Insert before the first lastColumn
          result = [
            ...result.slice(0, firstLastColumnIndex),
            ...orderedBeforeLast,
            ...result.slice(firstLastColumnIndex),
          ];
        } else {
          // No lastColumns in result yet, append to end
          result = [...result, ...orderedBeforeLast];
        }
      }

      // 4. Last columns go to end when added
      if (lastAdded.length > 0) {
        // Preserve order of lastColumns if multiple added simultaneously
        const orderedLast = lastAdded.sort((a, b) => {
          return lastColumns.indexOf(a) - lastColumns.indexOf(b);
        });
        result = [...result, ...orderedLast];
      }

      originalSetActiveColumns(result);
    },
    [activeColumns, originalSetActiveColumns, positioningRules]
  );

  return {
    activeColumns,
    setActiveColumns,
    columns,
  };
}
